import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { useRegister } from "@/hooks/useAuth";

export function RegisterPage() {
  const navigate = useNavigate();
  const { mutate, isPending, error } = useRegister();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) return;  // button is disabled when mismatched
    mutate(
      { username, password },
      { onSuccess: () => navigate("/", { replace: true }) }
    );
  }

  const mismatched = password.length > 0 && confirm.length > 0 && password !== confirm;
  const canSubmit = !isPending && !mismatched && username.length >= 3 && password.length >= 8;

  return (
    <>
      <Helmet>
        <title>Register — e621 Animation Finder</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex items-center justify-center px-6">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm space-y-4 bg-surface-1 border border-surface-3 rounded-lg p-6"
        >
          <h1 className="text-xl font-bold">Create account</h1>
          <p className="text-xs text-zinc-500">
            All app features work without an account. Registering lets you sync
            your blacklist and save searches across devices.
          </p>

          <Field label="Username" hint="3–32 chars · lowercase letters, digits, _ or -">
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={32}
              autoFocus
              className={inputClass}
            />
          </Field>

          <Field label="Password" hint="8+ chars">
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className={inputClass}
            />
          </Field>

          <Field label="Confirm password">
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className={inputClass}
            />
            {mismatched && (
              <p className="text-xs text-red-400 mt-1">Passwords don't match</p>
            )}
          </Field>

          {error && (
            <p className="text-xs text-red-400">{(error as Error).message}</p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-semibold py-2 rounded transition-colors"
          >
            {isPending ? "Creating…" : "Create account"}
          </button>

          <p className="text-xs text-zinc-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-accent-light hover:underline">
              Log in
            </Link>
            {" · "}
            <Link to="/" className="text-zinc-600 hover:text-zinc-400">
              Continue as guest
            </Link>
          </p>
        </form>
      </div>
    </>
  );
}

const inputClass =
  "w-full bg-surface-2 border border-surface-3 rounded px-3 py-2 text-sm focus:outline-none focus:border-accent transition-colors";

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
        {hint && <span className="text-[10px] text-zinc-600">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
